import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, Download, FileSpreadsheet, FileText, Loader2, Mail, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/layout/AdminShell";
import EmptyState from "../../components/EmptyState";
import { useAuth } from "../../context/AuthContext";
import { ReceiptsApi, ReportsApi, TeamApi, errorMessage } from "../../lib/api";
import { downloadBlob, formatDate, formatDateTime, formatMoney, isoDaysAgo, todayISO } from "../../lib/format";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useI18n } from "../../i18n";
import EmailReportDialog from "../../components/EmailReportDialog";

const Reports = () => {
  const { t } = useI18n();
  const { currency, categories, isAccountingFirm } = useAuth();
  const [filters, setFilters] = useState({
    date_from: isoDaysAgo(365),
    date_to: todayISO(),
    user_id: "all",
    category: "all",
    status: "counted",
  });
  const [preview, setPreview] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [monthly, setMonthly] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [payout, setPayout] = useState(null);
  const [payingUser, setPayingUser] = useState(null);
  const [sendingMonthly, setSendingMonthly] = useState(false);

  const query = useMemo(() => {
    const p = {};
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.user_id !== "all") p.user_id = filters.user_id;
    if (filters.category !== "all") p.category = filters.category;
    if (filters.status === "approved") p.status = "approved";
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPreview(await ReportsApi.preview(query));
    } catch (e) {
      toast.error(errorMessage(e, "Could not build the report"));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    TeamApi.members()
      .then((d) => setMembers(d.members || []))
      .catch(() => setMembers([]));
    loadMonthly();
  }, []);

  const loadMonthly = () =>
    ReportsApi.recipients()
      .then(setMonthly)
      .catch(() => setMonthly(null));

  const loadDocuments = useCallback(() => {
    ReportsApi.documents()
      .then((d) => setDocuments(d.documents || []))
      .catch(() => setDocuments([]));
  }, []);

  const loadPayout = useCallback(() => {
    ReportsApi.payout(query)
      .then(setPayout)
      .catch(() => setPayout(null));
  }, [query]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    const timer = setTimeout(loadPayout, 250);
    return () => clearTimeout(timer);
  }, [loadPayout]);

  const sendMonthly = async () => {
    setSendingMonthly(true);
    try {
      const res = await ReportsApi.monthlyRun();
      toast.success(t("reports.sentToast", { count: res.recipients?.length || 0 }), {
        description: res.document_no
          ? `${res.period} · ${t("reports.docNumber")} ${res.document_no}`
          : res.period,
      });
      loadMonthly();
      loadDocuments();
    } catch (e) {
      toast.error(errorMessage(e, "Could not send the monthly report"));
    } finally {
      setSendingMonthly(false);
    }
  };

  // a queued export finishes in the background; poll so the user gets an immediate link too
  const pollJob = (jobId) => {
    if (!jobId) return;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { job } = await ReportsApi.exportJob(jobId);
        if (job.status === "done") {
          toast.success(t("reports.exportReady"), {
            duration: 20000,
            action: {
              label: t("common.download"),
              onClick: () => window.open(job.download_url, "_blank"),
            },
          });
          return;
        }
        if (job.status === "failed") {
          toast.error(job.error || t("reports.exportFailed"));
          return;
        }
      } catch (e) {
        return;
      }
      if (tries < 40) setTimeout(tick, 3000);
    };
    setTimeout(tick, 3000);
  };

  const downloadPayout = async () => {
    setDownloading("payout");
    try {
      const { blob, disposition, rowCount } = await ReportsApi.payoutExport(query);
      const match = /filename="?([^";]+)"?/.exec(disposition || "");
      downloadBlob(blob, match?.[1] || "isplate.csv");
      toast[rowCount === 0 ? "info" : "success"](
        rowCount === 0 ? t("reports.payoutEmpty") : t("reports.payoutDownloaded")
      );
    } catch (e) {
      toast.error(errorMessage(e, "Export failed"));
    } finally {
      setDownloading(null);
    }
  };

  const payDriver = async (row) => {
    setPayingUser(row.user_id);
    try {
      const res = await ReceiptsApi.bulkMarkPaid(row.receipt_ids);
      toast.success(t("receipts.paidBulkToast", {
        count: res.paid,
        amount: formatMoney(res.amount, currency),
      }));
      loadPayout();
    } catch (e) {
      toast.error(errorMessage(e, "Could not mark refunds as paid"));
    } finally {
      setPayingUser(null);
    }
  };

  const download = async (format, presetOverride, layout) => {
    setDownloading(format);
    try {
      const res = await ReportsApi.download(
        format === "csv"
          ? { ...query, preset: presetOverride || "universal" }
          : layout
            ? { ...query, layout }
            : query,
        format
      );
      if (res.queued) {
        toast.info(t("reports.exportQueued", { email: res.email || "" }), { duration: 8000 });
        pollJob(res.job_id);
        return;
      }
      const { blob, disposition, documentNo, rowCount } = res;
      const match = /filename="?([^";]+)"?/.exec(disposition || "");
      downloadBlob(blob, match?.[1] || `expenses.${format}`);
      if (rowCount === 0) {
        toast.info(t("reports.emptyExport"));
      } else {
        toast.success(format === "csv" ? t("reports.downloadedCsv") : t("reports.downloadedPdf"), {
          description: documentNo ? `${t("reports.docNumber")} ${documentNo}` : undefined,
        });
        loadDocuments();
      }
    } catch (e) {
      toast.error(errorMessage(e, "Export failed"));
    } finally {
      setDownloading(null);
    }
  };

  const totals = preview?.totals || {};

  return (
    <AdminShell
      title={t("reports.title")}
      subtitle={t("reports.sub")}
      actions={
        <>
          {!isAccountingFirm ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEmailOpen(true)}
              data-testid="report-email-button"
            >
              <Mail className="mr-2 h-4 w-4" /> {t("reports.emailReport")}
            </Button>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="report-export-accounting-dropdown">
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                {t("reports.accountingExports")}
                <ChevronDown className="ml-1.5 h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>{t("receipts.exportLayout")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => download("csv", "synesis")}
                disabled={downloading === "csv"}
                data-testid="report-export-synesis"
              >
                {t("reports.presetSynesis")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => download("csv", "minimax")}
                disabled={downloading === "csv"}
                data-testid="report-export-minimax"
              >
                {t("reports.presetMinimax")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => download("csv", "universal")}
                disabled={downloading === "csv"}
                data-testid="report-export-csv"
              >
                {t("reports.presetUniversal")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" data-testid="report-export-pdf-dropdown">
                {downloading === "pdf" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                {t("reports.downloadPdf")}
                <ChevronDown className="ml-1.5 h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem
                onClick={() => download("pdf")}
                disabled={downloading === "pdf"}
                data-testid="report-export-pdf"
              >
                {t("reports.pdfStandard")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => download("pdf", undefined, "accounting")}
                disabled={downloading === "pdf"}
                data-testid="report-export-accounting-pdf"
              >
                {t("reports.pdfWithAttachments")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    >
      <div className="ll-card p-4" data-testid="reports-filter-bar">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("common.from")}</Label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))}
              data-testid="report-date-from"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("common.to")}</Label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))}
              data-testid="report-date-to"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("reports.employee")}</Label>
            <Select value={filters.user_id} onValueChange={(v) => setFilters((p) => ({ ...p, user_id: v }))}>
              <SelectTrigger data-testid="report-employee-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("receipts.everyone")}</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("common.category")}</Label>
            <Select value={filters.category} onValueChange={(v) => setFilters((p) => ({ ...p, category: v }))}>
              <SelectTrigger data-testid="report-category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("receipts.allCategories")}</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("reports.include")}</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
              <SelectTrigger data-testid="report-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="counted">{t("reports.includeAll")}</SelectItem>
                <SelectItem value="approved">{t("reports.includeApproved")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setFilters({
                date_from: isoDaysAgo(365),
                date_to: todayISO(),
                user_id: "all",
                category: "all",
                status: "counted",
              })
            }
            data-testid="report-reset-filters"
          >
            <RotateCcw className="mr-2 h-3.5 w-3.5" /> {t("reports.resetFilters")}
          </Button>
          <span className="text-xs text-muted-foreground">
            {preview
              ? t("reports.periodInfo", { count: preview.row_count, period: preview.period })
              : ""}
          </span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[
          [t("reports.receiptsCount"), totals.count ?? 0, false],
          [t("reports.netTotal"), totals.net ?? 0, true],
          [t("reports.vatTotal"), totals.vat ?? 0, true],
          [t("reports.grossTotal"), totals.gross ?? 0, true],
          ...(isAccountingFirm ? [] : [[t("reports.refunds"), totals.refund ?? 0, true]]),
        ].map(([label, value, money]) => (
          <div key={label} className="ll-card p-4" data-testid="report-total-card">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="ll-num mt-1 font-display text-xl font-semibold">
              {money ? formatMoney(value, currency) : value}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <div className="ll-card overflow-hidden">
          <div className="border-b px-4 py-3">
            <h2 className="font-display text-sm font-semibold">{t("reports.vatRecap")}</h2>
          </div>
          {preview?.vat_by_rate?.length ? (
            <Table data-testid="report-vat-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t("reports.vatRate")}</TableHead>
                  <TableHead className="text-right">{t("common.net")}</TableHead>
                  <TableHead className="text-right">{t("common.vat")}</TableHead>
                  <TableHead className="text-right">{t("common.gross")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.vat_by_rate.map((v) => (
                  <TableRow key={v.rate}>
                    <TableCell className="ll-num font-medium">{v.rate}%</TableCell>
                    <TableCell className="ll-num text-right">{formatMoney(v.net, currency)}</TableCell>
                    <TableCell className="ll-num text-right">{formatMoney(v.vat, currency)}</TableCell>
                    <TableCell className="ll-num text-right">{formatMoney(v.gross, currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="p-4 text-sm text-muted-foreground">{t("reports.noVatData")}</p>
          )}
        </div>

        {!isAccountingFirm ? (
        <div className="ll-card overflow-hidden">
          <div className="border-b px-4 py-3">
            <h2 className="font-display text-sm font-semibold">{t("reports.refundsPerEmployee")}</h2>
          </div>
          {preview?.by_employee?.length ? (
            <Table data-testid="report-employee-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t("common.employee")}</TableHead>
                  <TableHead className="text-right">{t("common.receipts")}</TableHead>
                  <TableHead className="text-right">{t("common.vat")}</TableHead>
                  <TableHead className="text-right">{t("common.refund")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.by_employee.map((e) => (
                  <TableRow key={e.employee}>
                    <TableCell className="font-medium">{e.employee}</TableCell>
                    <TableCell className="ll-num text-right">{e.receipts}</TableCell>
                    <TableCell className="ll-num text-right">{formatMoney(e.vat, currency)}</TableCell>
                    <TableCell className="ll-num text-right text-primary">
                      {formatMoney(e.refund, currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="p-4 text-sm text-muted-foreground">No refunds for this selection.</p>
          )}
        </div>
        ) : null}
      </div>

      <div className="mt-5">
        <h2 className="mb-2 font-display text-sm font-semibold">{t("reports.receiptDetail")}</h2>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-xl" />
            ))}
          </div>
        ) : preview?.rows?.length ? (
          <div className="ll-card ll-scroll overflow-x-auto">
            <Table data-testid="report-rows-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("common.employee")}</TableHead>
                  <TableHead>{t("common.vendor")}</TableHead>
                  <TableHead>{t("common.receiptNo")}</TableHead>
                  <TableHead>{t("common.category")}</TableHead>
                  <TableHead className="text-right">{t("common.net")}</TableHead>
                  <TableHead className="text-right">{t("common.vat")}</TableHead>
                  <TableHead className="text-right">{t("common.gross")}</TableHead>
                  <TableHead className="text-right">{t("common.refund")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(r.Date)}</TableCell>
                    <TableCell className="text-sm">{r.Employee}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm">{r.Vendor}</TableCell>
                    <TableCell className="font-mono text-xs">{r["Receipt no."] || "—"}</TableCell>
                    <TableCell className="text-sm">{r.Category}</TableCell>
                    <TableCell className="ll-num text-right">{formatMoney(r.Net, currency)}</TableCell>
                    <TableCell className="ll-num text-right">{formatMoney(r.VAT, currency)}</TableCell>
                    <TableCell className="ll-num text-right font-semibold">
                      {formatMoney(r.Gross, currency)}
                    </TableCell>
                    <TableCell className="ll-num text-right text-primary">
                      {formatMoney(r.Refund, currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState
            icon={FileText}
            title={t("reports.nothingTitle")}
            description={t("reports.nothingBody")}
          />
        )}
      </div>
      {!isAccountingFirm ? (
      <div className="ll-card mt-5 p-4" data-testid="monthly-report-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-base font-semibold">{t("reports.monthlyTitle")}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{t("reports.monthlySub")}</p>
            <p className="mt-2 text-sm">
              {monthly?.monthly_report?.enabled ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {t("common.yes")} ·{" "}
                  {(monthly.monthly_report.recipients || []).join(", ") || "—"}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                  {t("common.no")}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={sendMonthly}
              disabled={sendingMonthly}
              data-testid="monthly-send-now"
            >
              {sendingMonthly ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Mail className="mr-2 h-4 w-4" />
              )}
              {t("reports.sendNow")}
            </Button>
          </div>
        </div>
        {monthly?.history?.length ? (
          <div className="mt-4 overflow-hidden rounded-lg border">
            <Table data-testid="monthly-history-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t("reports.history")}</TableHead>
                  <TableHead>{t("common.recipients")}</TableHead>
                  <TableHead className="text-right">{t("common.receipts")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthly.history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="text-sm font-medium">{h.period}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {(h.recipients || []).join(", ")}
                    </TableCell>
                    <TableCell className="ll-num text-right">{h.row_count}</TableCell>
                    <TableCell className="text-sm capitalize">{h.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </div>
      ) : null}

      {payout && !isAccountingFirm ? (
        <div className="ll-card mt-5 overflow-hidden" data-testid="payout-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
            <div>
              <h2 className="font-display text-base font-semibold">{t("reports.payoutTitle")}</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">{t("reports.payoutSub")}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="ll-num text-sm font-semibold text-primary" data-testid="payout-total">
                {formatMoney(payout.total, currency)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadPayout}
                disabled={downloading === "payout"}
                data-testid="payout-export-button"
              >
                <Download className="mr-2 h-4 w-4" /> {t("reports.payoutExport")}
              </Button>
            </div>
          </div>
          {payout.rows?.length ? (
            <div className="ll-scroll overflow-x-auto">
              <Table data-testid="payout-table">
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead>{t("common.employee")}</TableHead>
                    <TableHead>{t("reports.payoutEmail")}</TableHead>
                    <TableHead className="text-right">{t("common.receipts")}</TableHead>
                    <TableHead className="text-right">{t("reports.payoutOwed")}</TableHead>
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payout.rows.map((r) => (
                    <TableRow key={r.user_id} data-testid={`payout-row-${r.user_id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.email || "—"}</TableCell>
                      <TableCell className="ll-num text-right">{r.receipts}</TableCell>
                      <TableCell className="ll-num text-right font-semibold text-primary">
                        {formatMoney(r.amount, r.currency || currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => payDriver(r)}
                          disabled={payingUser === r.user_id}
                          data-testid={`payout-mark-${r.user_id}`}
                        >
                          {payingUser === r.user_id ? (
                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                          ) : null}
                          {t("receipts.markPaid")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="px-4 py-6 text-sm text-muted-foreground" data-testid="payout-empty">
              {t("reports.payoutEmpty")}
            </p>
          )}
        </div>
      ) : null}

      <div className="ll-card mt-5 overflow-hidden" data-testid="report-documents-card">
        <div className="border-b px-4 py-3">
          <h2 className="font-display text-base font-semibold">{t("reports.docsTitle")}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{t("reports.docsSub")}</p>
        </div>
        {documents.length ? (
          <div className="ll-scroll overflow-x-auto">
          <Table data-testid="report-documents-table">
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead className="w-[150px]">{t("reports.docNumber")}</TableHead>
                <TableHead className="w-[150px]">{t("audit.when")}</TableHead>
                <TableHead>{t("reports.docKind")}</TableHead>
                <TableHead>{t("reports.docPeriod")}</TableHead>
                <TableHead className="text-right">{t("common.receipts")}</TableHead>
                <TableHead className="text-right">{t("common.gross")}</TableHead>
                <TableHead>{t("reports.docBy")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((d) => (
                <TableRow key={d.id} data-testid={`report-document-${d.number}`}>
                  <TableCell className="ll-num text-sm font-semibold text-primary">
                    {d.number}
                  </TableCell>
                  <TableCell className="ll-num whitespace-nowrap text-xs text-muted-foreground">
                    {formatDateTime(d.created_at)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {t(`reports.docKinds.${d.kind}`).startsWith("reports.docKinds.")
                      ? d.kind
                      : t(`reports.docKinds.${d.kind}`)}
                    {d.preset ? (
                      <span className="ml-1.5 text-xs text-muted-foreground">· {d.preset}</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.period || "—"}</TableCell>
                  <TableCell className="ll-num text-right">{d.row_count}</TableCell>
                  <TableCell className="ll-num text-right">
                    {formatMoney((d.totals || {}).gross, currency)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.created_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        ) : (
          <p className="px-4 py-6 text-sm text-muted-foreground" data-testid="report-documents-empty">
            {t("reports.docsEmpty")}
          </p>
        )}
      </div>

      <EmailReportDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        filters={query}
        onSent={() => {
          loadMonthly();
          loadDocuments();
        }}
      />
    </AdminShell>
  );
};

export default Reports;
